import { useState, useEffect } from "react";
import "./Payments.css";
import "./CRM.css"; // Reuse broad styles
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { formatCurrency } from "../utils/formatCurrency";
import { generateReceiptPDF } from "../utils/receiptGenerator";
import { Eye, FileEdit, Trash2, Search, Plus, FileText, Download } from "lucide-react";

function PaymentsScreen() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await dataService.getPayments();
      setPayments(response.data.data);
    } catch (error) {
      console.error("Failed to fetch payments", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleRecordPayment = () => {
    navigate("/payments/record");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const idIdx = headers.findIndex(h => h.toLowerCase().includes('payment') || h.toLowerCase().includes('id') || h.toLowerCase().includes('number'));
      const invoiceIdx = headers.findIndex(h => h.toLowerCase().includes('invoice'));
      const customerIdx = headers.findIndex(h => h.toLowerCase().includes('customer') || h.toLowerCase().includes('client'));
      const dateIdx = headers.findIndex(h => h.toLowerCase().includes('date'));
      const amountIdx = headers.findIndex(h => h.toLowerCase().includes('amount') || h.toLowerCase().includes('total'));
      const methodIdx = headers.findIndex(h => h.toLowerCase().includes('method') || h.toLowerCase().includes('type'));
      const typeIdx = headers.findIndex(h => h.toLowerCase().includes('payment type') || h.toLowerCase().includes('full/partial'));
      const statusIdx = headers.findIndex(h => h.toLowerCase().includes('status'));

      const newPayments = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        const amountVal = parseFloat((row[amountIdx] || '0').replace(/"/g, '').trim()) || 0;

        newPayments.push({
          paymentId: (row[idIdx] || `PAY-${Date.now().toString().slice(-4)}-${i}`).replace(/"/g, '').trim(),
          invoice: (row[invoiceIdx] || 'N/A').replace(/"/g, '').trim(),
          customer: (row[customerIdx] || 'Imported Customer').replace(/"/g, '').trim(),
          date: (row[dateIdx] || new Date().toISOString().split('T')[0]).replace(/"/g, '').trim(),
          amount: amountVal,
          method: (row[methodIdx] || 'Cash').replace(/"/g, '').trim(),
          type: (row[typeIdx] || 'Full').replace(/"/g, '').trim(),
          status: (row[statusIdx] || 'Completed').replace(/"/g, '').trim()
        });
      }

      if (newPayments.length > 0) {
        addToast(`Importing ${newPayments.length} payments...`, "success");
        try {
          for (const pay of newPayments) {
            await dataService.recordPayment(pay);
          }
          fetchPayments();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleView = (payment) => {
    setSelectedPayment(payment);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (payment) => {
    navigate(`/payments/edit/${payment._id}`);
  };

  const handleDeleteClick = (payment) => {
    setItemToDelete(payment);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deletePayment(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchPayments();
      addToast("Payment deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete payment: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updatePayment(selectedPayment._id, formData);
      setIsModalOpen(false);
      fetchPayments();
      addToast("Payment updated successfully", "success");
    } catch (error) {
      addToast("Failed to update payment: " + error.message, "error");
    }
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Payment Management</h1>
          <p>Track payment and outstanding balances</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('payments', 'recordPayments') && (
            <button className="primary-btn" onClick={handleRecordPayment}>
              <Plus size={18} /> Record payment
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Received</span>
            <h2 className="stat-value">{formatCurrency(payments.reduce((acc, p) => acc + p.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Full Payments</span>
            <h2 className="stat-value">{payments.filter(p => p.type === 'Full').length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Partial Payments</span>
            <h2 className="stat-value">{payments.filter(p => p.type === 'Partial').length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Transactions</span>
            <h2 className="stat-value">{payments.length}</h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Payment History</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search payments by ID, customer, or invoice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading payments...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments
                  .filter(p =>
                    p.paymentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.customer && p.customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (p.invoice && p.invoice.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .map((payment) => (
                    <tr key={payment._id}>
                      <td><span className="font-bold">{payment.paymentId}</span></td>
                      <td>{payment.invoice || "N/A"}</td>
                      <td>{payment.customer || "N/A"}</td>
                      <td>{new Date(payment.date).toLocaleDateString()}</td>
                      <td><span className="font-bold">{formatCurrency(payment.amount)}</span></td>
                      <td>{payment.method}</td>
                      <td>{payment.type}</td>
                      <td>
                        <span className={`status-pill ${payment.status.toLowerCase()}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="Download Receipt" onClick={() => generateReceiptPDF(payment)}>
                            <FileText size={16} />
                          </button>
                          <button className="icon-btn" title="View" onClick={() => handleView(payment)}>
                            <Eye size={16} />
                          </button>

                          {authService.hasPermission('payments', 'editPayments') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(payment)}>
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission('payments', 'deletePayments') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(payment)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {payments.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>No payment history found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Payment Details" : "Edit Payment"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'flex-end' }}>
              <button className="secondary-btn" onClick={() => generateReceiptPDF(selectedPayment)}>
                <FileText size={18} style={{ marginRight: '8px' }} /> Download Receipt
              </button>
              <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
            </div>
          )
        }
      >
        {modalMode === "view" && selectedPayment && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">Payment ID</span>
              <span className="detail-value">{selectedPayment.paymentId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Invoice</span>
              <span className="detail-value">{selectedPayment.invoice || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Customer</span>
              <span className="detail-value">{selectedPayment.customer || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date</span>
              <span className="detail-value">{new Date(selectedPayment.date).toLocaleDateString()}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Amount</span>
              <span className="detail-value" style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(selectedPayment.amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Method</span>
              <span className="detail-value">{selectedPayment.method}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedPayment.status?.toLowerCase()}`}>
                {selectedPayment.status}
              </span>
            </div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>Amount</label>
              <input type="number" name="amount" value={formData.amount || 0} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Method</label>
              <select name="method" value={formData.method || ""} onChange={handleFormChange}>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Check">Check</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || ""} onChange={handleFormChange}>
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
                <option value="Refunded">Refunded</option>
                <option value="Void">Void</option>
              </select>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Payment</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete payment record <strong>{itemToDelete?.paymentId}</strong>. This action cannot be undone.</p>
        </div>
      </Modal>
    </div>
  );
}

export default PaymentsScreen;
